import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, GraduationCap, Mail, Phone, Plus, Search, Trash2, User, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import instructorService from '../../services/instructorService';
import userService from '../../services/userService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import './InstructorsPage.css';

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  centres: [],
};

const InstructorsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, isAdmin } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [instructors, setInstructors] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCentreFilters, setSelectedCentreFilters] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const pageTexts = useMemo(() => t('instructors', { returnObjects: true }), [t]);
  const centreFilterOptions = useMemo(() => (
    centres.map((centre) => ({ value: String(centre.id), label: centre.name }))
  ), [centres]);

  const fallbackCentres = useMemo(() => {
    const userCentres = Array.isArray(user?.centres_details) ? user.centres_details : [];
    const primaryCentre = user?.centre && user?.centre_name
      ? [{ id: user.centre, name: user.centre_name }]
      : [];
    const byId = new Map([...userCentres, ...primaryCentre].map((centre) => [String(centre.id), centre]));
    return Array.from(byId.values());
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = search.trim() ? { search: search.trim() } : {};
      if (selectedCentreFilters.length) {
        params.centres = selectedCentreFilters.join(',');
      }
      const [instructorsResponse, centresResponse] = await Promise.all([
        instructorService.getAll(params),
        (isAdmin || user?.all_centres) ? userService.getCentres() : Promise.resolve(fallbackCentres),
      ]);

      setInstructors(instructorsResponse.results || instructorsResponse || []);
      setCentres(centresResponse.results || centresResponse || []);
    } catch (error) {
      console.error('Error loading instructors:', error);
      toast.error(pageTexts.errors?.load || t('instructors.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [fallbackCentres, isAdmin, pageTexts.errors?.load, search, selectedCentreFilters, t, toast, user?.all_centres]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingInstructor(null);
    setFormData({
      ...emptyForm,
      centres: centres.length === 1 ? [String(centres[0].id)] : [],
    });
    setModalOpen(true);
  };

  const openEdit = (instructor) => {
    setEditingInstructor(instructor);
    setFormData({
      first_name: instructor.first_name || '',
      last_name: instructor.last_name || '',
      email: instructor.email || '',
      phone: instructor.phone || '',
      centres: (instructor.centres || []).map(String),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingInstructor(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || formData.centres.length === 0) {
      toast.error(pageTexts.errors?.required || t('instructors.errors.required'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
      };

      if (editingInstructor) {
        await instructorService.update(editingInstructor.id, payload);
        toast.success(pageTexts.updated || t('instructors.updated'));
      } else {
        await instructorService.create(payload);
        toast.success(pageTexts.created || t('instructors.created'));
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error('Error saving instructor:', error);
      const detail = error.response?.data?.detail;
      const fieldError = Object.values(error.response?.data || {}).flat?.()?.[0];
      toast.error(detail || fieldError || pageTexts.errors?.save || t('instructors.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (instructor) => {
    if (!window.confirm(pageTexts.confirmDelete || t('instructors.confirmDelete'))) return;
    try {
      await instructorService.delete(instructor.id);
      toast.success(pageTexts.deleted || t('instructors.deleted'));
      fetchData();
    } catch (error) {
      console.error('Error deleting instructor:', error);
      toast.error(pageTexts.errors?.delete || t('instructors.errors.delete'));
    }
  };

  const toggleCentre = (centreId) => {
    const id = String(centreId);
    setFormData((prev) => ({
      ...prev,
      centres: prev.centres.includes(id)
        ? prev.centres.filter((item) => item !== id)
        : [...prev.centres, id],
    }));
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{pageTexts.loading || t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="instructors-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <GraduationCap size={32} />
            {pageTexts.title || t('instructors.title')}
          </h1>
          <p className="page-subtitle">{pageTexts.subtitle || t('instructors.subtitle')}</p>
        </div>
        {canCreate('instructors') && (
          <button type="button" className="btn-add" onClick={openCreate}>
            <Plus size={20} />
            {pageTexts.add || t('instructors.add')}
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={18} className="search-icon" />
          <input
            className="search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={pageTexts.searchPlaceholder || t('instructors.searchPlaceholder')}
          />
          {search && (
            <button type="button" className="search-clear-btn" onClick={() => setSearch('')} aria-label={t('common.clearSearch')}>
              <X size={14} />
            </button>
          )}
        </div>
        <div className="instructor-centre-filter">
          <MultiSelectDropdown
            options={centreFilterOptions}
            selectedValues={selectedCentreFilters}
            onChange={setSelectedCentreFilters}
            placeholder={pageTexts.filters?.allCentres || t('instructors.filters.allCentres')}
            label={pageTexts.filters?.centres || t('instructors.filters.centres')}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{pageTexts.table?.name || t('instructors.table.name')}</th>
              <th>{pageTexts.table?.contact || t('instructors.table.contact')}</th>
              <th>{pageTexts.table?.centres || t('instructors.table.centres')}</th>
              <th className="actions-column">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {instructors.length === 0 ? (
              <tr>
                <td colSpan="4">
                  <div className="empty-state">
                    <GraduationCap size={40} className="empty-icon" />
                    <h3>{pageTexts.empty || t('instructors.empty')}</h3>
                  </div>
                </td>
              </tr>
            ) : instructors.map((instructor) => (
              <tr key={instructor.id}>
                <td className="name-cell">
                  <User size={16} />
                  <span>{instructor.full_name || `${instructor.first_name} ${instructor.last_name}`}</span>
                </td>
                <td>
                  <div className="instructor-contact">
                    {instructor.email && <span><Mail size={14} /> {instructor.email}</span>}
                    {instructor.phone && <span><Phone size={14} /> {instructor.phone}</span>}
                    {!instructor.email && !instructor.phone && <span className="muted">{t('common.none')}</span>}
                  </div>
                </td>
                <td>
                  <div className="centres-list">
                    {(instructor.centres_details || []).map((centre) => (
                      <span key={centre.id} className="centre-chip">{centre.name}</span>
                    ))}
                  </div>
                </td>
                <td className="actions-cell">
                  {canEdit('instructors') && (
                    <button type="button" className="action-btn action-edit" onClick={() => openEdit(instructor)} title={t('common.edit')}>
                      <Edit size={16} />
                    </button>
                  )}
                  {canDelete('instructors') && (
                    <button type="button" className="action-btn action-delete" onClick={() => handleDelete(instructor)} title={t('common.delete')}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="dialog-overlay">
          <div className="instructor-dialog">
            <div className="dialog-header">
              <div className="dialog-title">
                <GraduationCap size={22} />
                <h2>{editingInstructor ? pageTexts.editTitle : pageTexts.addTitle}</h2>
              </div>
              <button type="button" className="dialog-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <form className="dialog-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">{pageTexts.form?.firstName} *</label>
                  <input id="first_name" className="ui-input" value={formData.first_name} onChange={(event) => setFormData({ ...formData, first_name: event.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">{pageTexts.form?.lastName} *</label>
                  <input id="last_name" className="ui-input" value={formData.last_name} onChange={(event) => setFormData({ ...formData, last_name: event.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">{pageTexts.form?.email}</label>
                  <input id="email" type="email" className="ui-input" value={formData.email} onChange={(event) => setFormData({ ...formData, email: event.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{pageTexts.form?.phone}</label>
                  <input id="phone" className="ui-input" value={formData.phone} onChange={(event) => setFormData({ ...formData, phone: event.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>{pageTexts.form?.centres} *</label>
                <div className="centres-picker">
                  {centres.map((centre) => (
                    <label key={centre.id} className="centre-option">
                      <input
                        type="checkbox"
                        checked={formData.centres.includes(String(centre.id))}
                        onChange={() => toggleCentre(centre.id)}
                      />
                      <span>{centre.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="dialog-actions ui-dialog-actions">
                <button type="button" className="btn-secondary ui-btn-secondary" onClick={closeModal} disabled={saving}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary ui-btn-primary" disabled={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorsPage;
